import { Sparkles } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";
import type { AgentThoughtProcess_formattedMessage$data } from "src/components/ui/chat/__generated__/AgentThoughtProcess_formattedMessage.graphql";
import { Markdown } from "src/components/ui/Markdown";

import type { SkillCluster_toolCall$key } from "./__generated__/SkillCluster_toolCall.graphql";
import { SkillSheet } from "./SkillSheet";
import { type CustomSkillSummaryType, sanitizeSkillName, useCustomSkillsByName } from "./useCustomSkills";
import { getThoughtProcessItemDisplayText } from "./v2Utils";

type ThoughtProcessItem = AgentThoughtProcess_formattedMessage$data["agentThoughtProcess"][number];

// Reuses the existing V2AgentThoughtProcessRenderer_toolCall fragment (already
// spread by AgentThoughtProcess_message / _formattedMessage), which selects
// toolUse.input.rawInput — we read `rawInput.skill` to label and gate the chip.
const skillFragment = graphql`
  fragment SkillCluster_toolCall on GraphQLMessageToolCall {
    toolUse {
      id
      input {
        __typename
        ... on GenericToolInput {
          rawInput
        }
      }
    }
  }
`;

function safelyParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractSkillName(rawInput: unknown): string | null {
  const parsed = typeof rawInput === "string" ? safelyParseJson(rawInput) : rawInput;
  if (parsed && typeof parsed === "object" && "skill" in parsed) {
    const v = (parsed as { skill?: unknown }).skill;
    return typeof v === "string" ? v : null;
  }
  return null;
}

const getDisplayText = getThoughtProcessItemDisplayText;

interface SkillItemProps {
  item: ThoughtProcessItem;
  customSkillsByName: Map<string, CustomSkillSummaryType>;
  shouldBlink: boolean;
  onClickSkill: (skillName: string) => void;
}

/**
 * Renders one skill entry. Calls useFragment internally to resolve the skill
 * name, then drops itself entirely (chip + any attached thinkingSummary) if the
 * skill name is not in the org's CustomSkill set.
 *
 * Visibility is decided here per-item (requires useFragment to read the skill
 * name), so first/last positioning across visible items isn't known in the
 * parent without a second pass. Uniform `mt-3` spacing keeps rendering correct
 * regardless of which items end up dropping out.
 */
const SkillItem: React.FC<SkillItemProps> = React.memo(function SkillItem({
  item,
  customSkillsByName,
  shouldBlink,
  onClickSkill,
}) {
  const data = useFragment(
    skillFragment,
    (item.toolUse ?? null) as SkillCluster_toolCall$key | null,
  );
  const rawInput =
    data?.toolUse?.input?.__typename === "GenericToolInput"
      ? data.toolUse.input.rawInput
      : null;
  const skillName = extractSkillName(rawInput);
  // Sanitize for the lookup so map keys (also sanitized) match regardless of
  // how the raw skill name was emitted by the agent.
  const lookupKey = skillName ? sanitizeSkillName(skillName) : null;
  const isCustomSkill = !!lookupKey && customSkillsByName.has(lookupKey);

  const displayText = getDisplayText(item);

  // Built-in skill: hide the chip but keep any thinkingSummary attached to the item.
  // The narration text is generic agent activity, not an acknowledgment of a
  // specific built-in skill.
  if (!isCustomSkill) {
    if (!displayText) return null;
    return (
      <div className="flex flex-col gap-1 mt-3">
        <div className="flex gap-2 items-start">
          <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>●</span>
          <div className="text-foreground flex-1">
            <Markdown>{displayText}</Markdown>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 mt-3">
      {displayText && (
        <div className="flex gap-2 items-start">
          <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>●</span>
          <div className="text-foreground flex-1">
            <Markdown>{displayText}</Markdown>
          </div>
        </div>
      )}
      <div className={cn("flex gap-2 items-start", !displayText && "mt-0")}>
        <span className="text-muted-foreground flex-shrink-0 invisible">●</span>
        <button
          type="button"
          onClick={() => onClickSkill(lookupKey!)}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-popover hover:bg-secondary text-gray-300 text-xs rounded px-3 py-1.5 border border-border text-left w-fit transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
          <span className="font-semibold">Skill</span>
          <span className="text-gray-600">|</span>
          <span className="text-gray-300 break-all">{skillName}</span>
        </button>
      </div>
    </div>
  );
});

interface SkillClusterContentProps {
  tools: Array<{ item: ThoughtProcessItem; index: number }>;
  shouldBlink: boolean;
}

const SkillClusterContent: React.FC<SkillClusterContentProps> = ({ tools, shouldBlink }) => {
  const customSkillsByName = useCustomSkillsByName();
  // Retain the last-opened skill so the SkillSheet stays mounted (and can run
  // its exit animation) after `isOpen` flips to false on close.
  const [openedSkill, setOpenedSkill] = React.useState<CustomSkillSummaryType | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClickSkill = React.useCallback(
    (skillName: string) => {
      const skill = customSkillsByName.get(skillName);
      if (!skill) return;
      // Set both synchronously so the sheet appears on the same render as the click.
      setOpenedSkill(skill);
      setIsOpen(true);
    },
    [customSkillsByName],
  );

  return (
    <>
      {tools.map((tool, idx) => (
        <SkillItem
          key={tool.item.toolUse?.toolUse?.id ?? `skill-${idx}`}
          item={tool.item}
          customSkillsByName={customSkillsByName}
          shouldBlink={shouldBlink}
          onClickSkill={handleClickSkill}
        />
      ))}
      {openedSkill && (
        <SkillSheet
          skill={openedSkill}
          isOpen={isOpen}
          onOpenChange={(open) => {
            if (!open) setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

interface SkillClusterProps {
  tools: Array<{ item: ThoughtProcessItem; index: number }>;
  shouldBlink: boolean;
}

/**
 * Skill cluster — renders only the Skill invocations whose names match
 * organization-defined CustomSkills. Built-in skills are silently dropped
 * (chip and any attached thinkingSummary). Wrapped in Suspense so loading
 * the customSkills query never blocks the rest of the thought process.
 */
export const SkillCluster: React.FC<SkillClusterProps> = (props) => (
  <React.Suspense fallback={null}>
    <SkillClusterContent {...props} />
  </React.Suspense>
);
